import React, { useId } from 'react';
import { cx } from './cx';

export interface FieldProps {
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  /** Button beside the control (e.g. "Get code" next to a code input); hint and error stay under both. */
  action?: React.ReactNode;
  id?: string;
  className?: string;
  /** Exactly one control (Input, TextArea, Select …); it receives id and ARIA wiring. */
  children: React.ReactElement<Record<string, unknown>>;
}

/** Label + control + hint + error, wired for screen readers. */
export default function Field({ label, hint, error, required, action, id, className, children }: FieldProps) {
  const auto = useId();
  const controlId = id ?? (children.props.id as string | undefined) ?? `${auto}-control`;
  const hintId = hint ? `${auto}-hint` : undefined;
  const errorId = error ? `${auto}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const control = React.cloneElement(children, {
    id: controlId,
    'aria-describedby': describedBy,
    ...(error ? { invalid: true, 'aria-invalid': true } : {}),
    ...(required ? { required: true } : {}),
  });
  return (
    <div className={cx('flex min-w-0 flex-col gap-1.5', className)}>
      <label htmlFor={controlId} className="font-ui text-small font-medium text-secondary">
        {label}
        {required ? <span aria-hidden className="text-danger"> *</span> : null}
      </label>
      {action ? (
        <div className="flex min-w-0 items-center gap-2">
          <div className="min-w-0 flex-1">{control}</div>
          <div className="flex shrink-0 items-center">{action}</div>
        </div>
      ) : (
        control
      )}
      {hint ? (
        <p id={hintId} className="m-0 font-ui text-small text-tertiary">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="m-0 font-ui text-small text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
